import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeftRight, ArrowRight, Search } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { ENTRY_MODE_LABEL, fmtDate, fmtDateTime, type EntryMode } from '../lib/industria'
import {
  ORDER_STATUS_BADGE, ORDER_STATUS_LABEL,
  type TraceBackwardRow, type TraceForwardRow,
} from '../lib/producao'

type Direction = 'backward' | 'forward'

// As duas consultas saem da mesma tabela de apontamento, lidas em direções
// opostas. É o que se compra com a disciplina de bipar volume no chão de fábrica.
export function IndustriaRastreabilidadePage() {
  const [params, setParams] = useSearchParams()
  const [direction, setDirection] = useState<Direction>('backward')
  const [term, setTerm] = useState('')
  const [query, setQuery] = useState('')

  // Vindo do botão "Rastrear" da OP.
  useEffect(() => {
    const lote = params.get('lote')
    if (lote) {
      setDirection('backward')
      setTerm(lote)
      setQuery(lote)
    }
  }, [params])

  const { data: backward = [], isFetching: loadingBack } = useQuery<TraceBackwardRow[]>({
    queryKey: ['trace', 'backward', query],
    queryFn: async () => (await api.get('/traceability/backward', { params: { lotNumber: query } })).data,
    enabled: direction === 'backward' && !!query,
  })

  const { data: forward = [], isFetching: loadingForward } = useQuery<TraceForwardRow[]>({
    queryKey: ['trace', 'forward', query],
    queryFn: async () => (await api.get('/traceability/forward', { params: { lotNumber: query } })).data,
    enabled: direction === 'forward' && !!query,
  })

  function run() {
    setQuery(term.trim())
    if (params.get('lote')) setParams({}, { replace: true })
  }

  const loading = loadingBack || loadingForward

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Rastreabilidade</h2>
        <p className="text-sm text-gray-500">
          Do consumidor de volta à nota do fornecedor — e o caminho inverso, quando o problema
          aparece do outro lado
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => { setDirection('backward'); setQuery('') }}
              className={`rounded-xl border p-3 text-left transition-colors ${
                direction === 'backward'
                  ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="block text-sm font-medium text-gray-900">
                Para trás — lote do produto
              </span>
              <span className="mt-1 block text-xs leading-snug text-gray-500">
                O que entrou neste lote: cada componente, com lote, fornecedor e nota.
              </span>
            </button>
            <button
              type="button"
              onClick={() => { setDirection('forward'); setQuery('') }}
              className={`rounded-xl border p-3 text-left transition-colors ${
                direction === 'forward'
                  ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="block text-sm font-medium text-gray-900">
                Para frente — lote do componente
              </span>
              <span className="mt-1 block text-xs leading-snug text-gray-500">
                Onde foi parar: que ordens usaram, quanto sobrou e em quais volumes.
              </span>
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {direction === 'backward' ? 'Lote do produto final' : 'Lote do componente'}
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  className="pl-9 font-mono"
                  placeholder={direction === 'backward' ? '260817-01' : 'lote do fornecedor ou da WS'}
                  value={term}
                  onChange={e => setTerm(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') run() }}
                />
              </div>
            </div>
            <Button onClick={run} loading={loading} disabled={!term.trim()}>
              <ArrowLeftRight className="h-4 w-4" /> Rastrear
            </Button>
          </div>
        </CardContent>
      </Card>

      {!!query && direction === 'backward' && (
        backward.length === 0 && !loading ? (
          <p className="py-10 text-center text-sm text-gray-400">
            Nenhuma ordem com o lote {query}.
          </p>
        ) : backward.map(o => (
          <Card key={o.id}>
            <CardHeader>
              <div>
                <span className="text-sm font-semibold text-gray-900">
                  {o.productName} · lote <span className="font-mono">{o.lotNumber}</span>
                </span>
                <span className="block text-xs text-gray-500">
                  <Link to={`/industria/ops/${o.id}`} className="font-mono text-blue-600 hover:underline">
                    {o.number}
                  </Link>
                  {' · '}{o.tenantName}
                  {o.qtyProduced ? ` · ${o.qtyProduced.toLocaleString('pt-BR')} un produzidas` : ''}
                  {o.expiryDate ? ` · validade ${fmtDate(o.expiryDate)}` : ''}
                </span>
              </div>
              <Badge variant={ORDER_STATUS_BADGE[o.status]}>{ORDER_STATUS_LABEL[o.status]}</Badge>
            </CardHeader>
            <CardContent className="p-0">
              {o.components.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-gray-400">Nenhum apontamento nesta ordem</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-2.5 text-left font-medium">Componente</th>
                        <th className="px-4 py-2.5 text-left font-medium">Lote</th>
                        <th className="px-4 py-2.5 text-left font-medium">Fornecedor</th>
                        <th className="px-4 py-2.5 text-left font-medium">Documento</th>
                        <th className="px-4 py-2.5 text-left font-medium">Entrada</th>
                        <th className="px-4 py-2.5 text-right font-medium">Qtd</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {o.components.map((c, i) => (
                        <tr key={`${c.lotId ?? 'sem'}-${c.kind}-${i}`} className={c.kind === 'perda' ? 'text-gray-400' : undefined}>
                          <td className="px-4 py-2.5">
                            <span className="text-gray-900">{c.materialName}</span>
                            <span className="block font-mono text-xs text-gray-400">
                              {c.materialCode}{c.kind === 'perda' ? ' · perda' : ''}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-gray-700">
                            {c.lotNumber ?? <span className="text-amber-600">sem lote</span>}
                            {c.supplierLot && (
                              <span className="block text-xs text-gray-400">forn.: {c.supplierLot}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">{c.supplierName ?? '—'}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                            {c.docNumber ?? '—'}
                            {c.docKey && <span className="block text-gray-400">{c.docKey}</span>}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {c.entryMode ? ENTRY_MODE_LABEL[c.entryMode as EntryMode] : '—'}
                            {c.receivedAt && (
                              <span className="block text-xs text-gray-400">{fmtDateTime(c.receivedAt)}</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                            {c.quantity.toLocaleString('pt-BR')} <span className="text-xs text-gray-400">{c.unit}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {!!query && direction === 'forward' && (
        forward.length === 0 && !loading ? (
          <p className="py-10 text-center text-sm text-gray-400">
            Nenhum lote de componente chamado {query}.
          </p>
        ) : forward.map(l => (
          <Card key={l.id}>
            <CardHeader>
              <div>
                <span className="text-sm font-semibold text-gray-900">
                  {l.materialName} · lote <span className="font-mono">{l.lotNumber}</span>
                </span>
                <span className="block text-xs text-gray-500">
                  {l.supplierName ?? 'sem fornecedor'}
                  {l.docNumber ? ` · NF ${l.docNumber}` : ''}
                  {' · '}{ENTRY_MODE_LABEL[l.entryMode as EntryMode]}
                  {' · '}{l.ownerName ?? 'WS (próprio)'}
                  {' · recebido '}{fmtDateTime(l.receivedAt)}
                </span>
              </div>
              <Badge variant={l.remaining > 0 ? 'warning' : 'default'}>
                {l.remaining.toLocaleString('pt-BR')} {l.unit} ainda parados
              </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Produtos que levaram este lote
                </p>
                {l.orders.length === 0 ? (
                  <p className="text-sm text-gray-400">Nenhuma ordem consumiu este lote ainda.</p>
                ) : (
                  <div className="space-y-2">
                    {l.orders.map(o => (
                      <div key={o.orderId} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 px-3 py-2 text-sm">
                        <Link to={`/industria/ops/${o.orderId}`} className="font-mono text-blue-600 hover:underline">
                          {o.orderNumber}
                        </Link>
                        <ArrowRight className="h-3 w-3 text-gray-300" />
                        <span className="text-gray-900">{o.productName}</span>
                        {o.productLot && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
                            lote {o.productLot}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">{o.tenantName}</span>
                        <Badge variant={ORDER_STATUS_BADGE[o.status]}>{ORDER_STATUS_LABEL[o.status]}</Badge>
                        <span className="ml-auto font-mono text-gray-700">
                          {o.quantity.toLocaleString('pt-BR')} {l.unit}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Onde o saldo está parado
                </p>
                {l.volumes.length === 0 ? (
                  <p className="text-sm text-gray-400">Nenhum volume em aberto deste lote.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {l.volumes.map(v => (
                      <span key={v.id} className="rounded-lg border border-gray-200 px-2.5 py-1 font-mono text-xs text-gray-700">
                        {v.code} · {v.qtyCurrent.toLocaleString('pt-BR')} {l.unit}
                        {v.locationCode ? ` · ${v.locationCode}` : ''}
                        <span className="ml-1 text-gray-400">{v.status}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  )
}
